import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";

export const GoogleButton = ({ testId }) => (
  <button
    data-testid={testId}
    type="button"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="w-full flex items-center justify-center gap-3 border border-border bg-white hover:bg-slate-50 text-navy font-medium py-3.5 text-sm transition-colors"
  >
    <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/></svg>
    Continue with Google
  </button>
);

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      const dest = ["admin", "super_admin"].includes(data.role) ? "/admin"
        : data.role === "professional" ? "/pro/dashboard" : "/dashboard";
      navigate(params.get("next") || dest);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      <AppHeader />
      <div className="max-w-md mx-auto px-6 py-16 lg:py-24">
        <p className="uppercase tracking-[0.25em] text-flame font-heading font-bold text-sm mb-3">Welcome back</p>
        <h1 className="font-heading font-black tracking-tighter text-navy text-4xl">Log in</h1>
        <form onSubmit={onSubmit} className="mt-10 space-y-4 bg-white border border-border p-8" data-testid="login-form">
          <div>
            <label className="text-sm font-medium text-navy block mb-2">Email</label>
            <input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full h-14 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy block mb-2">Password</label>
            <input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full h-14 border border-border px-4 text-sm focus:outline-none focus:ring-2 focus:ring-flame focus:border-transparent" />
          </div>
          {error && <p data-testid="login-error" className="text-red-600 text-sm">{error}</p>}
          <button data-testid="login-submit-btn" disabled={loading}
            className="w-full bg-flame hover:bg-flame-dark disabled:opacity-60 text-white font-heading font-bold uppercase tracking-wider text-sm py-4 transition-colors">
            {loading ? "Logging in…" : "Log In"}
          </button>
          <div className="flex items-center gap-3 py-1">
            <span className="flex-1 h-px bg-border" /><span className="text-xs text-slate-400">OR</span><span className="flex-1 h-px bg-border" />
          </div>
          <GoogleButton testId="login-google-btn" />
        </form>
        <p className="text-sm text-slate-500 mt-6">
          New to Prime Pro?{" "}
          <Link to="/join" data-testid="login-join-link" className="text-flame font-medium hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
